import * as React from 'react'
import { useRef } from 'react'
import { Zoom } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import {
  MuteButton,
  PauseButton,
  ResponsiveImage,
  RightArrow,
  RotateClockwise,
  SelectMark,
  ViewportSizes,
  timingSlower,
  useIsViewport,
} from '@syconium/little-miss-figgy'

import { resizeShopifyImageSource } from '../../../../../../lib/utils/images'
import { useTranslation } from '../../../../../_providers/TranslationProvider.client'
import { NextDialogProvider, renderWithNoPadding } from '../../../../dialogs/NextDialog.client'
import { GalleryImageStateContainer } from '../providers/GalleryImageStateContainer'

import {
  CaretLeft,
  CaretRight,
  CloseButton,
  Container,
  ImageZoomDialog,
  LeftArrowWrap,
  RightArrowWrap,
  StyledPaginationPageIndicator,
  StyledVideoButtonsWrapper,
  Video,
  ZoomSwiperWrapper,
} from './styles'

import type { Swiper as SwiperType } from 'swiper'

const zoomedImageSize = 2400

export const ImageZoom: React.FC = () => {
  const {
    allAssets,
    currentAssetIndex,
    hideImageZoom,
    imageDescription,
    isImageZoomShown,
    setCurrentAssetIndex,
  } = GalleryImageStateContainer.useContainer()

  const {
    pages: {
      product: { gallery: strings },
    },
  } = useTranslation()

  const isSmallViewport = useIsViewport(ViewportSizes.small)
  const swiperRef = useRef<SwiperType>(null)

  React.useEffect(() => {
    if (swiperRef.current && swiperRef.current.realIndex !== currentAssetIndex) {
      swiperRef.current.slideToLoop(currentAssetIndex, 0)
    }
  }, [currentAssetIndex])

  return (
    <NextDialogProvider open={isImageZoomShown}>
      <ImageZoomDialog
        analyticsTitle='Image Zoom'
        variant='default'
        render={renderWithNoPadding}
        content={<Container />}
        onClose={hideImageZoom}
        backdropTiming={timingSlower}
        dialogTiming={timingSlower}
      >
        <CloseButton onClick={hideImageZoom} aria-label={strings.closeZoom}>
          <SelectMark height='16px' width='16px' stroke={isSmallViewport ? 'white' : undefined} />
        </CloseButton>

        <LeftArrowWrap
          onClick={() => swiperRef.current?.slidePrev()}
          aria-label={strings.previousImage}
        >
          {isSmallViewport ? (
            <CaretLeft />
          ) : (
            <RightArrow height='24px' width='28px' rotateClockwise={RotateClockwise.Half} />
          )}
        </LeftArrowWrap>
        <RightArrowWrap
          onClick={() => swiperRef.current?.slideNext()}
          aria-label={strings.nextImage}
        >
          {isSmallViewport ? <CaretRight /> : <RightArrow height='24px' width='28px' />}
        </RightArrowWrap>

        <StyledPaginationPageIndicator
          currentIndex={currentAssetIndex + 1}
          totalCount={allAssets.length}
        />

        <ZoomSwiperWrapper>
          <Swiper
            loop={allAssets.length > 1}
            modules={[Zoom]}
            onSwiper={swiper => {
              swiperRef.current = swiper
              swiper.slideToLoop(currentAssetIndex, 0)
            }}
            onSlideChange={swiper => setCurrentAssetIndex(swiper.realIndex)}
            slidesPerView={1}
            zoom={{
              maxRatio: 2,
              minRatio: 1,
            }}
          >
            {allAssets.map((asset, index) => (
              <SwiperSlide key={'url' in asset ? asset.url + index : asset.key}>
                {asset.__typename === 'ProductCatalogImage' ||
                asset.__typename === 'KitColoredImage' ? (
                  <div className='swiper-zoom-container'>
                    <ResponsiveImage
                      alt={
                        asset.altText || imageDescription || `${index + 1} of ${allAssets.length}`
                      }
                      src={resizeShopifyImageSource({
                        height: zoomedImageSize,
                        source: asset.url,
                        width: zoomedImageSize,
                      })}
                      aspectRatios={{ sm: 1, md: 1, lg: 1 }}
                      loading='lazy'
                      widths={{
                        unit: 'px',
                        sm: zoomedImageSize,
                        md: zoomedImageSize,
                        lg: zoomedImageSize,
                      }}
                    />
                  </div>
                ) : asset.__typename === 'ProductCatalogVideo' ? (
                  <ZoomedProductVideo displayMuteButton={asset.type === 'UGC'} source={asset.url} />
                ) : (
                  <>{asset.children}</>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </ZoomSwiperWrapper>
      </ImageZoomDialog>
    </NextDialogProvider>
  )
}

const ZoomedProductVideo: React.FC<{ source: string; displayMuteButton?: boolean }> = ({
  source,
  displayMuteButton,
}) => {
  const {
    magnolia: {
      general: { playVideo, pauseVideo, muteVideo, unmuteVideo },
    },
  } = useTranslation()
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const [isPaused, setIsPaused] = React.useState<boolean>(false)
  const [isMuted, setIsMuted] = React.useState<boolean>(true)
  const toggleVideoPause = () => {
    if (isPaused) {
      videoRef.current?.play()
      setIsPaused(false)
    } else {
      videoRef.current?.pause()
      setIsPaused(true)
    }
  }
  const toggleVideoMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted
    }
    setIsMuted(!isMuted)
  }
  return (
    <>
      <Video autoPlay muted playsInline loop ref={videoRef}>
        <source src={source} type='video/mp4' />
      </Video>
      <StyledVideoButtonsWrapper>
        <PauseButton
          onClick={toggleVideoPause}
          pauseText={pauseVideo}
          playText={playVideo}
          paused={isPaused}
          aria-label={isPaused ? playVideo : pauseVideo}
        />
        {displayMuteButton && (
          <MuteButton
            onClick={toggleVideoMute}
            isMuted={isMuted}
            mutedText={muteVideo}
            notMutedText={unmuteVideo}
          />
        )}
      </StyledVideoButtonsWrapper>
    </>
  )
}
